import IImage from '@/interfaces/IImage';

/**
 * Deduplicates images based on their `src` property.
 * 
 * This function ensures that all `IImage` objects across two arrays are unique by `src`.
 * It returns two arrays:
 * - `dedupedPrimary`: images originally from the primary list (if duplicates, prioritized here)
 * - `dedupedSecondary`: images only found in the secondary list (and not in the primary)
 * 
 * @param {IImage[]} primary - The primary array of images (e.g., `product.images`)
 * @param {IImage[]} secondary - The secondary array of images (e.g., `description.images`)
 * @returns {{ dedupedPrimary: IImage[], dedupedSecondary: IImage[] }} 
 * An object containing the deduplicated `primary` and `secondary` image arrays
 */
export function deduplicateImages(
    primary: IImage[],
    secondary: IImage[]
): { dedupedPrimary: IImage[]; dedupedSecondary: IImage[] } {
    const all = [...primary, ...secondary];
    const seen = new Map<string, IImage>();

    for (const img of all) {
        if (!seen.has(img.src)) {
            seen.set(img.src, img);
        }
    }

    const dedupedPrimary: IImage[] = [];
    const dedupedSecondary: IImage[] = [];

    const primarySrcSet = new Set(primary.map(p => p.src));
    for (const img of seen.values()) {
        if (primarySrcSet.has(img.src)) {
            dedupedPrimary.push(img);
        } else {
            dedupedSecondary.push(img);
        }
    }

    return { dedupedPrimary, dedupedSecondary };
}
